import { ApplicationConfig, provideAppInitializer, inject, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { AppStateService } from './shared/services/app-state.service';
import { AuthOAuthService } from './shared/services/oauth.service';
import ApPreset from '../preset';

/**
 * Initialize OAuth authentication on app startup
 * Checks for existing tokens in localStorage and initializes user if valid
 */
async function initializeAuthOnStartup(
  oauthService: AuthOAuthService,
  appStateService: AppStateService
): Promise<void> {
  try {
    // Check stored tokens (no network call needed)
    const hasSession = await firstValueFrom(oauthService.tryLogin());

    // Initialize user data if we have a valid session
    if (hasSession) {
      await appStateService.initializeUserOnStartup();
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    console.error('[INIT] ✗ Initialization error:', errorMsg);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(
      withInterceptors([authInterceptor]),
      withInterceptorsFromDi()
    ),
    MessageService,

    provideAppInitializer(async () => {
      const oauthService = inject(AuthOAuthService);
      const appStateService = inject(AppStateService);
      await initializeAuthOnStartup(oauthService, appStateService);
    }),

    providePrimeNG({
      ripple: true,
      theme: {
        preset: ApPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'app-styles, another-css-library',
          },
        },
      },
    }),
  ]
};
